export interface WorkWeekPeriod {
  workWeekStart: string;
  workWeekEnd: string;
  payDate: string;
  payYear: number;
  periodNumber: number;
}

export function getWorkWeekContainingDate(date: Date): { start: Date; end: Date } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const localDate = new Date(year, month, day);

  const dayOfWeek = localDate.getDay();

  const sunday = new Date(year, month, day);
  sunday.setDate(sunday.getDate() - dayOfWeek);

  const saturday = new Date(sunday);
  saturday.setDate(saturday.getDate() + 6);

  return {
    start: sunday,
    end: saturday
  };
}

export function getPayDateForSaturday(saturday: Date): Date {
  const year = saturday.getFullYear();
  const month = saturday.getMonth();
  const day = saturday.getDate();
  const dayOfWeek = saturday.getDay();

  if (dayOfWeek !== 6) {
    throw new Error('getPayDateForSaturday requires a Saturday');
  }

  const payDate = new Date(year, month, day);
  payDate.setDate(payDate.getDate() + 6);

  return payDate;
}

export function calculatePayrollPeriod(referenceDate: Date = new Date()): WorkWeekPeriod {
  const workWeek = getWorkWeekContainingDate(referenceDate);
  const payDate = getPayDateForSaturday(workWeek.end);

  const payYear = workWeek.end.getFullYear();

  const periodNumber = calculatePeriodNumber(workWeek.end);

  return {
    workWeekStart: formatDate(workWeek.start),
    workWeekEnd: formatDate(workWeek.end),
    payDate: formatDate(payDate),
    payYear,
    periodNumber
  };
}

export function calculatePeriodNumber(workWeekEndDate: Date): number {
  const year = workWeekEndDate.getFullYear();
  const month = workWeekEndDate.getMonth();
  const day = workWeekEndDate.getDate();

  const localDate = new Date(year, month, day, 12, 0, 0);

  let firstSaturday = new Date(year, 0, 1, 12, 0, 0);
  const dayOfWeek = firstSaturday.getDay();

  if (dayOfWeek <= 6) {
    firstSaturday.setDate(firstSaturday.getDate() + (6 - dayOfWeek));
  } else {
    firstSaturday.setDate(firstSaturday.getDate() + 6);
  }

  const daysSinceFirstSaturday = Math.floor(
    (localDate.getTime() - firstSaturday.getTime()) / (1000 * 60 * 60 * 24)
  );

  const weeksSinceFirst = Math.floor(daysSinceFirstSaturday / 7);

  return weeksSinceFirst + 1;
}

export function getPayrollPeriodsForYear(year: number): WorkWeekPeriod[] {
  const periods: WorkWeekPeriod[] = [];

  const firstDayOfYear = new Date(year, 0, 1, 12, 0, 0);
  const lastDayOfYear = new Date(year, 11, 31, 12, 0, 0);

  let currentDate = new Date(year, 0, 1, 12, 0, 0);
  currentDate.setDate(currentDate.getDate() - 7);

  while (currentDate <= lastDayOfYear) {
    currentDate.setDate(currentDate.getDate() + 7);

    const period = calculatePayrollPeriod(currentDate);

    if (period.payYear === year) {
      const exists = periods.some(p => p.payDate === period.payDate);
      if (!exists) {
        periods.push(period);
      }
    }
  }

  return periods.sort((a, b) =>
    parseLocalDate(a.payDate).getTime() - parseLocalDate(b.payDate).getTime()
  );
}

export function getNextPayPeriods(count: number = 4, fromDate: Date = new Date()): WorkWeekPeriod[] {
  const periods: WorkWeekPeriod[] = [];
  const year = fromDate.getFullYear();
  const month = fromDate.getMonth();
  const day = fromDate.getDate();
  let currentDate = new Date(year, month, day, 12, 0, 0);

  for (let i = 0; i < count; i++) {
    const period = calculatePayrollPeriod(currentDate);
    periods.push(period);

    currentDate.setDate(currentDate.getDate() + 7);
  }

  return periods;
}

export function getPeriodsStartingWithNextPayday(count: number = 4): WorkWeekPeriod[] {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();
  const todayLocal = new Date(year, month, day);

  const searchStartDate = new Date(year, month, day, 12, 0, 0);
  searchStartDate.setDate(searchStartDate.getDate() - 21);

  const allPeriods: WorkWeekPeriod[] = [];
  let currentSearchDate = new Date(searchStartDate);

  for (let i = 0; i < 30; i++) {
    const period = calculatePayrollPeriod(currentSearchDate);
    allPeriods.push(period);
    currentSearchDate.setDate(currentSearchDate.getDate() + 7);
  }

  const futurePeriods = allPeriods.filter(period => {
    const payDate = parseLocalDate(period.payDate);
    return payDate >= todayLocal;
  });

  if (futurePeriods.length === 0) {
    return getNextPayPeriods(count, today);
  }

  return futurePeriods.slice(0, count);
}

export function getPreviousPayPeriods(count: number = 4, fromDate: Date = new Date()): WorkWeekPeriod[] {
  const periods: WorkWeekPeriod[] = [];
  const year = fromDate.getFullYear();
  const month = fromDate.getMonth();
  const day = fromDate.getDate();
  let currentDate = new Date(year, month, day, 12, 0, 0);

  for (let i = 0; i < count; i++) {
    currentDate.setDate(currentDate.getDate() - 7);
    const period = calculatePayrollPeriod(currentDate);
    periods.unshift(period);
  }

  return periods;
}

export function canEmployeeReceivePaycheck(
  employeeStartDate: string,
  payPeriodEnd: string
): boolean {
  const startDate = parseLocalDate(employeeStartDate);
  const periodEnd = parseLocalDate(payPeriodEnd);

  const workWeek = getWorkWeekContainingDate(periodEnd);

  return startDate <= workWeek.end;
}

export function hasEmployeeCompletedWorkWeekAsOfDate(
  employeeStartDate: string,
  asOfDate: string
): boolean {
  const startDate = parseLocalDate(employeeStartDate);
  const checkDate = parseLocalDate(asOfDate);

  if (checkDate.getDay() !== 6) {
    return false;
  }

  const workWeek = getWorkWeekContainingDate(checkDate);

  return startDate <= workWeek.end;
}

export function getFirstEligiblePayPeriod(employeeStartDate: string): WorkWeekPeriod {
  const startDate = parseLocalDate(employeeStartDate);

  const firstWorkWeek = getWorkWeekContainingDate(startDate);

  const saturday = firstWorkWeek.end;

  const payDate = getPayDateForSaturday(saturday);

  const payYear = saturday.getFullYear();
  const periodNumber = calculatePeriodNumber(saturday);

  return {
    workWeekStart: formatDate(firstWorkWeek.start),
    workWeekEnd: formatDate(firstWorkWeek.end),
    payDate: formatDate(payDate),
    payYear,
    periodNumber
  };
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function parseLocalDateForDisplay(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatLocalDate(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  return `${month}/${day}/${year}`;
}

export function validatePayrollPeriodLogic() {
  const testDate = new Date('2024-12-23');
  const period = calculatePayrollPeriod(testDate);

  const expectedWorkStart = '2024-12-22';
  const expectedWorkEnd = '2024-12-28';
  const expectedPayDate = '2025-01-03';
  const expectedPayYear = 2024;

  return {
    passed:
      period.workWeekStart === expectedWorkStart &&
      period.workWeekEnd === expectedWorkEnd &&
      period.payDate === expectedPayDate &&
      period.payYear === expectedPayYear,
    expected: {
      workWeekStart: expectedWorkStart,
      workWeekEnd: expectedWorkEnd,
      payDate: expectedPayDate,
      payYear: expectedPayYear
    },
    actual: period
  };
}

export interface PayPeriod {
  start: string;
  end: string;
}

export function getCurrentPayPeriod(
  frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly',
  periodStart: string,
  payday: number
): PayPeriod {
  const today = new Date();
  const startDate = parseLocalDate(periodStart);

  if (frequency === 'weekly') {
    const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const weeksSinceStart = Math.floor(daysSinceStart / 7);
    const currentPeriodStart = new Date(startDate);
    currentPeriodStart.setDate(currentPeriodStart.getDate() + (weeksSinceStart * 7));
    const currentPeriodEnd = new Date(currentPeriodStart);
    currentPeriodEnd.setDate(currentPeriodEnd.getDate() + 6);

    return {
      start: formatDate(currentPeriodStart),
      end: formatDate(currentPeriodEnd)
    };
  }

  if (frequency === 'biweekly') {
    const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const periodsSinceStart = Math.floor(daysSinceStart / 14);
    const currentPeriodStart = new Date(startDate);
    currentPeriodStart.setDate(currentPeriodStart.getDate() + (periodsSinceStart * 14));
    const currentPeriodEnd = new Date(currentPeriodStart);
    currentPeriodEnd.setDate(currentPeriodEnd.getDate() + 13);

    return {
      start: formatDate(currentPeriodStart),
      end: formatDate(currentPeriodEnd)
    };
  }

  if (frequency === 'semimonthly') {
    const currentDay = today.getDate();
    if (currentDay < 15) {
      return {
        start: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        end: formatDate(new Date(today.getFullYear(), today.getMonth(), 15))
      };
    } else {
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      return {
        start: formatDate(new Date(today.getFullYear(), today.getMonth(), 16)),
        end: formatDate(new Date(today.getFullYear(), today.getMonth(), lastDay))
      };
    }
  }

  if (frequency === 'monthly') {
    return {
      start: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      end: formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0))
    };
  }

  return {
    start: formatDate(today),
    end: formatDate(today)
  };
}

export function getAllPayPeriods(
  frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly',
  periodStart: string,
  payday: number,
  count: number = 6
): PayPeriod[] {
  const periods: PayPeriod[] = [];
  const currentPeriod = getCurrentPayPeriod(frequency, periodStart, payday);
  const currentStart = parseLocalDate(currentPeriod.start);

  for (let i = 0; i < count; i++) {
    if (frequency === 'weekly') {
      const start = new Date(currentStart);
      start.setDate(start.getDate() - (i * 7));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      periods.unshift({
        start: formatDate(start),
        end: formatDate(end)
      });
    } else if (frequency === 'biweekly') {
      const start = new Date(currentStart);
      start.setDate(start.getDate() - (i * 14));
      const end = new Date(start);
      end.setDate(end.getDate() + 13);
      periods.unshift({
        start: formatDate(start),
        end: formatDate(end)
      });
    } else if (frequency === 'semimonthly') {
      const today = new Date();
      const month = today.getMonth() - Math.floor(i / 2);
      const year = today.getFullYear() + Math.floor(month / 12);
      const adjustedMonth = ((month % 12) + 12) % 12;

      if (i % 2 === 0) {
        const lastDay = new Date(year, adjustedMonth + 1, 0).getDate();
        periods.unshift({
          start: formatDate(new Date(year, adjustedMonth, 16)),
          end: formatDate(new Date(year, adjustedMonth, lastDay))
        });
      } else {
        periods.unshift({
          start: formatDate(new Date(year, adjustedMonth, 1)),
          end: formatDate(new Date(year, adjustedMonth, 15))
        });
      }
    } else if (frequency === 'monthly') {
      const today = new Date();
      const month = today.getMonth() - i;
      const year = today.getFullYear() + Math.floor(month / 12);
      const adjustedMonth = ((month % 12) + 12) % 12;
      periods.unshift({
        start: formatDate(new Date(year, adjustedMonth, 1)),
        end: formatDate(new Date(year, adjustedMonth + 1, 0))
      });
    }
  }

  return periods;
}
